import { z } from 'zod';
import {
  ANIMATED_WEBP_LIMITS,
  assertAnimationMatches,
  hasWebpSignature,
  inspectAnimatedWebp,
  type AnimatedWebpInfo,
  type DeclaredAnimation,
} from './animatedWebp';

const identifier = z.string().regex(/^[a-z0-9][a-z0-9_]{1,63}$/);
const sha256 = z.string().regex(/^[a-f0-9]{64}$/);
const terms = z.array(z.string().trim().min(1).max(128)).max(128).default([]);
const dimension = z.number().int().positive().max(8192);
const relativePath = z
  .string()
  .min(1)
  .refine(
    (value) =>
      !value.startsWith('/') &&
      !value.includes('\\') &&
      !value.split('/').some((part) => part === '..' || part === '.' || !part),
    'Asset paths must be relative to the selected payload folder',
  );

const categorySchema = z.object({
  id: identifier,
  displayName: z.string().trim().min(1).max(128),
  sortOrder: z.number().int().min(0).max(999),
});

/** How a release is applied: REPLACE swaps the whole library, APPEND adds to the live one (STX-PLAN D16). */
export const STICKER_RELEASE_MODES = ['REPLACE', 'APPEND'] as const;
export type StickerReleaseMode = (typeof STICKER_RELEASE_MODES)[number];

/**
 * A release mode reported by the server is untyped JSON: narrow it to a mode this page has wording for.
 * Anything else (a later server mode, a hand-edited status) is undefined, so callers fall back instead of
 * indexing a copy table with a key it does not have.
 */
export function knownReleaseMode(value: unknown): StickerReleaseMode | undefined {
  return STICKER_RELEASE_MODES.find((mode) => mode === value);
}

/** `png` (or absent) is a static sticker; `webp-anim` is an animated loop (a staged release, never the single-image upload). */
export const STICKER_FORMATS = ['png', 'webp-anim'] as const;
export type StickerFormat = (typeof STICKER_FORMATS)[number];

const stickerSchema = z.object({
  id: z.string().regex(/^[a-z0-9][a-z0-9_-]{1,63}__[a-z0-9][a-z0-9._-]{0,63}$/),
  name: z.string().trim().min(1).max(128),
  categoryId: identifier,
  sortOrder: z.number().int().min(0).max(100000),
  sourceId: z.string().trim().min(1).max(128).optional(),
  keywords: z.array(z.string().trim().min(1).max(128)).min(1).max(256),
  tags: terms,
  style: z.string().max(128).nullable().optional(),
  styleName: z.string().max(128).nullable().optional(),
  relatedCategoryIds: z.array(identifier).max(64).default([]),
  originalWidth: dimension,
  originalHeight: dimension,
  // The server tightens this to 1,000,000 bytes for an animated source (checked below, per format).
  bytes: z
    .number()
    .int()
    .positive()
    .max(2 * 1024 * 1024),
  // Omitted for static PNGs so existing manifests keep serialising byte-for-byte as before. The server accepts a
  // JSON null for the three facts on a static row (it drops nulls when it hashes the manifest), so the browser
  // does too; `format` is not nullable because the server rejects `"format": null`.
  format: z.enum(STICKER_FORMATS).optional(),
  frameCount: z.number().int().min(1).max(ANIMATED_WEBP_LIMITS.maxFrames).nullish(),
  durationMs: z.number().int().min(1).max(ANIMATED_WEBP_LIMITS.maxTotalMs).nullish(),
  loopCount: z.number().int().min(0).max(0).nullish(),
  sha256,
  thumbnailWidth: z.number().int().positive().max(512),
  thumbnailHeight: z.number().int().positive().max(512),
  thumbnailBytes: z
    .number()
    .int()
    .positive()
    .max(256 * 1024),
  thumbnailSha256: sha256,
  sourcePath: relativePath,
  thumbnailPath: relativePath,
});

const manifestSchema = z.object({
  releaseId: z.string().regex(/^[a-z0-9][a-z0-9_-]{1,63}$/),
  // Absent means REPLACE; omitted from the request in that case so existing releases are unchanged.
  mode: z.enum(STICKER_RELEASE_MODES).optional(),
  version: z.string().min(1).max(128),
  categories: z.array(categorySchema).min(1).max(64),
  stickers: z.array(stickerSchema).min(1).max(5000),
});

export type StickerImportManifest = z.infer<typeof manifestSchema>;
export type StickerImportAsset = StickerImportManifest['stickers'][number];
export type StickerReleaseManifest = Omit<StickerImportManifest, 'stickers'> & {
  stickers: Omit<StickerImportAsset, 'sourcePath' | 'thumbnailPath'>[];
};

/** The format of a manifest entry's source image; manifests that predate animation have no `format`. */
export function stickerFormat(asset: Pick<StickerImportAsset, 'format'>): StickerFormat {
  return asset.format ?? 'png';
}

export function isAnimatedAsset(asset: Pick<StickerImportAsset, 'format'>): boolean {
  return stickerFormat(asset) === 'webp-anim';
}

/** Cross-field rules that zod's per-field shapes cannot say (STX-PLAN §9.2 envelope; errors name the sticker). */
function assertAnimationFields(sticker: StickerImportAsset) {
  const limits = ANIMATED_WEBP_LIMITS;
  if (!isAnimatedAsset(sticker)) {
    if (sticker.frameCount != null || sticker.durationMs != null || sticker.loopCount != null)
      throw new Error(`Animation fields on a static sticker: ${sticker.id}`);
    return;
  }
  if (sticker.frameCount == null || sticker.durationMs == null || sticker.loopCount == null)
    throw new Error(`Animated sticker needs frameCount, durationMs and loopCount: ${sticker.id}`);
  if (sticker.frameCount < limits.minFrames)
    throw new Error(`Animated sticker needs at least ${limits.minFrames} frames: ${sticker.id}`);
  if (sticker.durationMs < limits.minTotalMs)
    throw new Error(`Animated sticker loop is shorter than ${limits.minTotalMs} ms: ${sticker.id}`);
  if (sticker.bytes > limits.maxBytes)
    throw new Error(`Animated sticker is larger than ${limits.maxBytes} bytes: ${sticker.id}`);
  const long = Math.max(sticker.originalWidth, sticker.originalHeight);
  const short = Math.min(sticker.originalWidth, sticker.originalHeight);
  if (long > limits.maxLongEdge || short < limits.minShortEdge)
    throw new Error(
      `Animated sticker canvas must have a long edge of at most ${limits.maxLongEdge} and a short edge of at least ${limits.minShortEdge}: ${sticker.id}`,
    );
  if (sticker.originalWidth * sticker.originalHeight * sticker.frameCount > limits.maxVolume)
    throw new Error(`Animated sticker decodes to too many pixels: ${sticker.id}`);
}

export function parseStickerImportManifest(input: unknown): StickerImportManifest {
  const manifest = manifestSchema.parse(input);
  const categories = new Set(manifest.categories.map((item) => item.id));
  if (categories.size !== manifest.categories.length)
    throw new Error('Duplicate group ids in manifest');
  const ids = new Set<string>();
  const paths = new Set<string>();
  const hashes = new Set<string>();
  for (const sticker of manifest.stickers) {
    if (ids.has(sticker.id)) throw new Error(`Duplicate sticker id: ${sticker.id}`);
    ids.add(sticker.id);
    if (hashes.has(sticker.sha256)) throw new Error(`Duplicate sticker artwork: ${sticker.id}`);
    hashes.add(sticker.sha256);
    if (sticker.originalWidth * sticker.originalHeight > 16777216)
      throw new Error(`Decoded image is too large: ${sticker.id}`);
    assertAnimationFields(sticker);
    if (!categories.has(sticker.categoryId))
      throw new Error(`Unknown group: ${sticker.categoryId}`);
    if (!sticker.id.startsWith(`${sticker.categoryId}__`))
      throw new Error(`Sticker id does not match its group: ${sticker.id}`);
    for (const related of sticker.relatedCategoryIds) {
      if (!categories.has(related)) throw new Error(`Unknown related group: ${related}`);
    }
    for (const path of [sticker.sourcePath, sticker.thumbnailPath]) {
      if (paths.has(path)) throw new Error(`Duplicate asset path: ${path}`);
      paths.add(path);
    }
  }
  return manifest;
}

export function releaseManifest(manifest: StickerImportManifest): StickerReleaseManifest {
  return {
    releaseId: manifest.releaseId,
    version: manifest.version,
    ...(manifest.mode ? { mode: manifest.mode } : {}),
    categories: manifest.categories,
    stickers: manifest.stickers.map(({ sourcePath, thumbnailPath, ...asset }) => {
      void sourcePath;
      void thumbnailPath;
      // A null fact on a static row means "absent": leave the key out, like a manifest that never had it.
      if (asset.frameCount == null) delete asset.frameCount;
      if (asset.durationMs == null) delete asset.durationMs;
      if (asset.loopCount == null) delete asset.loopCount;
      return asset;
    }),
  };
}

export function mapPayloadFiles(files: File[]): Map<string, File> {
  const result = new Map<string, File>();
  for (const file of files) {
    const path = file.webkitRelativePath || file.name;
    result.set(path, file);
    const slash = path.indexOf('/');
    if (slash >= 0) result.set(path.slice(slash + 1), file);
  }
  return result;
}

export function missingPayloadFiles(
  manifest: StickerImportManifest,
  files: Map<string, File>,
): string[] {
  return manifest.stickers
    .flatMap((asset) => [asset.sourcePath, asset.thumbnailPath])
    .filter((path) => !files.has(path));
}

/** Review figures for the dialog: how many animated stickers an import carries and how long the longest loops. */
export function animationSummary(manifest: StickerImportManifest) {
  const animated = manifest.stickers.filter(isAnimatedAsset);
  return {
    count: animated.length,
    longestLoopMs: Math.max(0, ...animated.map((asset) => asset.durationMs ?? 0)),
    largestBytes: Math.max(0, ...animated.map((asset) => asset.bytes)),
  };
}

/**
 * Check one local file against its manifest entry before it is uploaded: byte count, container signature for the
 * expected format, SHA-256 and — for an animated WebP — the §9.2 structure and the declared animation facts.
 * Resolves with the inspected animation for a `webp-anim` source (for progress text), undefined for a PNG.
 * The release dialog runs it for every animated source before it creates the release (a mismatch of the declared
 * facts is a manifest error that cannot be repaired under the same releaseId) and again before each upload.
 */
export async function validatePayloadFile(
  file: File,
  bytes: number,
  expectedHash: string,
  expectedFormat: StickerFormat = 'png',
  declared?: DeclaredAnimation,
): Promise<AnimatedWebpInfo | undefined> {
  if (file.size !== bytes) throw new Error(`${file.name}: file size differs from the manifest`);
  const data = await file.arrayBuffer();
  const signature = new Uint8Array(data, 0, Math.min(8, data.byteLength));
  const animated = expectedFormat === 'webp-anim';
  if (animated) {
    if (!hasWebpSignature(new Uint8Array(data, 0, Math.min(12, data.byteLength)))) {
      throw new Error(`${file.name}: expected an animated WebP file`);
    }
  } else {
    const png = [137, 80, 78, 71, 13, 10, 26, 10];
    if (signature.length !== 8 || png.some((byte, index) => signature[index] !== byte)) {
      throw new Error(`${file.name}: expected a PNG file`);
    }
  }
  const digest = await crypto.subtle.digest('SHA-256', data);
  const hash = Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, '0'),
  ).join('');
  if (hash !== expectedHash) throw new Error(`${file.name}: checksum differs from the manifest`);
  if (!animated) return undefined;
  try {
    const info = inspectAnimatedWebp(new Uint8Array(data));
    if (declared) assertAnimationMatches(info, declared);
    return info;
  } catch (reason) {
    throw new Error(
      `${file.name}: ${reason instanceof Error ? reason.message : 'invalid animation'}`,
    );
  }
}
